import type {
  LowStockItemRow,
  StockPolicyItemType,
  StockPolicyListRow,
  UpsertStockPolicyPayload,
} from "@/modules/inventory/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type StockPoliciesListParams = {
  branchId?: string;
  itemType?: StockPolicyItemType;
  itemId?: string;
  search?: string;
  categoryId?: string;
  isActive?: boolean;
  belowMinOnly?: boolean;
  onHandBranchId?: string;
  limit?: number;
  offset?: number;
};

export type StockPoliciesListResult = {
  items: StockPolicyListRow[];
  total: number;
};

function buildQuery(params?: StockPoliciesListParams): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  if (params.branchId) sp.set("branchId", params.branchId);
  if (params.itemType) sp.set("itemType", params.itemType);
  if (params.itemId) sp.set("itemId", params.itemId);
  if (params.search?.trim()) sp.set("search", params.search.trim());
  if (params.categoryId) sp.set("categoryId", params.categoryId);
  if (params.isActive !== undefined) {
    sp.set("isActive", params.isActive ? "true" : "false");
  }
  if (params.belowMinOnly) sp.set("belowMinOnly", "true");
  if (params.onHandBranchId) sp.set("onHandBranchId", params.onHandBranchId);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export const inventoryStockPoliciesApi = {
  list(params?: StockPoliciesListParams): Promise<StockPoliciesListResult> {
    return fetcher.get(
      `${config.apiUrl}/inventory/stock-policies${buildQuery(params)}`,
    );
  },

  get(id: string, onHandBranchId?: string): Promise<StockPolicyListRow> {
    const qs = onHandBranchId
      ? `?onHandBranchId=${encodeURIComponent(onHandBranchId)}`
      : "";
    return fetcher.get(
      `${config.apiUrl}/inventory/stock-policies/${id}${qs}`,
    );
  },

  upsert(payload: UpsertStockPolicyPayload): Promise<StockPolicyListRow> {
    return fetcher.put(`${config.apiUrl}/inventory/stock-policies/upsert`, payload);
  },

  bulkUpsert(items: UpsertStockPolicyPayload[]): Promise<{
    updated: number;
    rows: StockPolicyListRow[];
  }> {
    return fetcher.post(`${config.apiUrl}/inventory/stock-policies/bulk-upsert`, {
      items,
    });
  },

  patch(
    id: string,
    body: {
      minQty?: number;
      targetQty?: number | null;
      note?: string | null;
      isActive?: boolean;
    },
  ): Promise<StockPolicyListRow> {
    return fetcher.patch(`${config.apiUrl}/inventory/stock-policies/${id}`, body);
  },

  deactivate(id: string): Promise<{ id: string; isActive: false }> {
    return fetcher.delete(`${config.apiUrl}/inventory/stock-policies/${id}`);
  },

  copyFromGlobal(body: {
    branchId: string;
    belowMinOnly?: boolean;
    itemType?: StockPolicyItemType;
  }): Promise<{
    copied: number;
    skippedExistingOverride: number;
    rows: StockPolicyListRow[];
  }> {
    return fetcher.post(
      `${config.apiUrl}/inventory/stock-policies/copy-from-global`,
      body,
    );
  },

  async exportCsv(params?: StockPoliciesListParams): Promise<Blob> {
    const qs = buildQuery(params);
    const res = await fetch(
      `${config.apiUrl}/inventory/stock-policies/export${qs}`,
      { credentials: "include" },
    );
    if (!res.ok) throw new Error("EXPORT_FAILED");
    return res.blob();
  },

  importCsv(csv: string): Promise<{ updated: number; rows: StockPolicyListRow[] }> {
    return fetcher.post(`${config.apiUrl}/inventory/stock-policies/import`, {
      csv,
    });
  },

  lowStock(params: {
    branchId: string;
    itemType?: StockPolicyItemType;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: LowStockItemRow[]; total: number }> {
    const sp = new URLSearchParams();
    sp.set("branchId", params.branchId);
    if (params.itemType) sp.set("itemType", params.itemType);
    if (params.categoryId) sp.set("categoryId", params.categoryId);
    if (params.limit != null) sp.set("limit", String(params.limit));
    if (params.offset != null) sp.set("offset", String(params.offset));
    return fetcher.get(
      `${config.apiUrl}/inventory/stock-policies/low-stock?${sp.toString()}`,
    );
  },
};

export function formatStockPolicyApiError(e: unknown): string {
  const err = e as { message?: string; error?: string };
  const code = err?.error ?? err?.message ?? "";
  switch (code) {
    case "INVALID_QTY":
      return "ປະລິມານບໍ່ຖືກຕ້ອງ (min > 0, target ≥ min)";
    case "INVALID_ITEM":
      return "ລາຍການບໍ່ຖືກຕ້ອງ ຫຼື ປິດໃຊ້";
    case "NOT_FOUND":
      return "ບໍ່ພົບເກັນໄຂ";
    case "BRANCH_NOT_FOUND":
      return "ບໍ່ພົບສາຂາ";
    default:
      return err?.message ?? "ດຳເນີນການລົ້ມເຫຼວ";
  }
}
